import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, TrendingUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface UpgradePromptProps {
  type: 'invoiceScans' | 'transactions' | 'apiCalls';
  dismissible?: boolean;
  className?: string;
}

export function UpgradePrompt({ type, dismissible = true, className = '' }: UpgradePromptProps) {
  const navigate = useNavigate();
  const { limits, nextTier, nextTierPrice, tier } = usePlanLimits();
  const { usage, loading } = useUsageTracking();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when month changes
  useEffect(() => {
    const storageKey = `upgrade-prompt-dismissed-${type}`;
    const dismissedData = localStorage.getItem(storageKey);
    if (dismissedData) {
      const { month } = JSON.parse(dismissedData);
      const currentMonth = new Date().toISOString().slice(0, 7);
      if (month !== currentMonth) {
        localStorage.removeItem(storageKey);
        setDismissed(false);
      } else {
        setDismissed(true);
      }
    }
  }, [type]);

  if (loading || dismissed || !nextTier) return null;

  const getUsageData = () => {
    switch (type) {
      case 'invoiceScans':
        return { 
          current: usage.invoiceScans, 
          limit: limits.invoiceScans,
          label: 'invoice scans'
        };
      case 'transactions':
        return { 
          current: usage.transactionsImported, 
          limit: limits.transactions,
          label: 'transaction imports'
        };
      case 'apiCalls':
        return { 
          current: usage.apiCalls, 
          limit: limits.apiCallsPerDay,
          label: 'API calls'
        };
    }
  };

  const { current, limit, label } = getUsageData();
  
  if (typeof limit === 'string') return null; // 'unlimited'
  
  const percentage = (current / limit) * 100;
  const isNearLimit = percentage >= 80 && percentage < 100;
  const isAtLimit = percentage >= 100;

  if (!isNearLimit && !isAtLimit) return null;

  const handleDismiss = () => {
    if (dismissible) {
      const storageKey = `upgrade-prompt-dismissed-${type}`;
      const currentMonth = new Date().toISOString().slice(0, 7);
      localStorage.setItem(storageKey, JSON.stringify({ month: currentMonth }));
      setDismissed(true);
    }
  };

  return (
    <Alert 
      variant={isAtLimit ? 'destructive' : 'default'} 
      className={`relative ${isAtLimit ? '' : 'border-warning bg-warning/10'} ${className}`}
    >
      {dismissible && (
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-md hover:bg-background/50 transition-colors"
        >
          <X size={16} />
        </button>
      )}
      <AlertTriangle className={`h-4 w-4 ${isAtLimit ? '' : 'text-warning'}`} />
      <AlertTitle className="pr-8">
        {isAtLimit ? `${label} limit reached` : `Approaching ${label} limit`}
      </AlertTitle>
      <AlertDescription className="mt-2">
        <p className="mb-3">
        {isAtLimit 
            ? `You've used all ${limit.toLocaleString()} ${label} on your ${tier} plan.`
            : `You've used ${current.toLocaleString()} of ${limit.toLocaleString()} ${label} (${Math.round(percentage)}%).`
          }
          {' '}Upgrade to {nextTier} for {nextTier === 'pro' || nextTier === 'business' ? 'unlimited' : 'higher'} limits.
        </p>
        <Button 
          onClick={() => navigate('/pricing')} 
          size="sm"
          variant={isAtLimit ? 'default' : 'outline'}
        >
          <TrendingUp size={16} className="mr-2" />
          Upgrade to {nextTier} ({nextTierPrice})
        </Button>
      </AlertDescription>
    </Alert>
  );
}

// Inline prompt for use in specific feature areas
export function InlineUpgradePrompt({ 
  type, 
  children 
}: { 
  type: 'invoiceScans' | 'transactions' | 'apiCalls';
  children?: React.ReactNode;
}) {
  const { limits, nextTier, tier } = usePlanLimits();
  const { usage, loading } = useUsageTracking();
  const navigate = useNavigate();

  if (loading || !nextTier) return null;

  const getUsageData = () => {
    switch (type) {
      case 'invoiceScans':
        return { current: usage.invoiceScans, limit: limits.invoiceScans };
      case 'transactions':
        return { current: usage.transactionsImported, limit: limits.transactions };
      case 'apiCalls':
        return { current: usage.apiCalls, limit: limits.apiCallsPerDay };
    }
  };

  const { current, limit } = getUsageData();
  
  if (typeof limit === 'string') return null; // 'unlimited'
  
  const remaining = limit - current;
  const percentage = (current / limit) * 100;
  
  if (percentage < 80) return null;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={percentage >= 100 ? 'text-destructive' : 'text-warning'}>
        {remaining <= 0 ? 'Limit reached' : `${remaining} remaining`}
      </span>
      <button 
        onClick={() => navigate('/pricing')}
        className="text-primary hover:underline font-medium"
      >
        Upgrade
      </button>
      {children}
    </div>
  );
}
